"use strict";
/**
 * Anthony's Debt Eliminator feedback (close_spec, 2026-10-28; J1, J6, J8, J9, J14), for
 * the "Payments that look like debts" list on both apps. Shared so the web and the phone
 * show the same words and apply the same rules (rule 17).
 *
 * Whether a payment is a bill by default (J9, the mortgage) is decided by the server:
 * each candidate carries `treat_as_bill`. Nothing here reads a balance: a checking
 * payment carries none, so the totals are payments a month, never an amount owed
 * (contract DE01, "do not infer loan terms from checking payments").
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEBT_ADD_INSTEAD_COPY = exports.DEBT_PAID_AS_BILL_NOTE_COPY = exports.DEBT_PAID_AS_BILL_HEADING_COPY = exports.DEBT_NONE_ADDED_FOUND_BODY_COPY = exports.DEBT_NONE_ADDED_TITLE_COPY = void 0;
exports.displayLenderName = displayLenderName;
exports.foundDebtMonthly = foundDebtMonthly;
exports.summarizeFoundDebts = summarizeFoundDebts;
exports.debtFoundSummaryCopy = debtFoundSummaryCopy;
/** J1: no debts added yet, while payments that look like debts are listed above. */
exports.DEBT_NONE_ADDED_TITLE_COPY = "No debts added yet";
exports.DEBT_NONE_ADDED_FOUND_BODY_COPY = "Add the payments above that you still owe. With each one's balance, rate and minimum, you'll see your payoff plan.";
/** J9: the mortgage, listed apart from the payoff candidates. */
exports.DEBT_PAID_AS_BILL_HEADING_COPY = "Paid as a bill";
exports.DEBT_PAID_AS_BILL_NOTE_COPY = "Most people pay a mortgage as a bill, so it stays with your bills. Add it as a debt only if you want to plan paying it off early.";
exports.DEBT_ADD_INSTEAD_COPY = "Add as a debt instead";
// J6: short forms that stay upper case when a name is shown in normal case.
const KEEP_UPPER = new Set([
    "USAA",
    "LLC",
    "NA",
    "N.A.",
    "FCU",
    "CU",
    "USA",
    "US",
    "BMW",
    "GM",
    "HSBC",
    "PNC",
    "TD",
    "SBA",
    "FHA",
    "VA",
    "HELOC",
]);
/**
 * J6: a lender name in normal case, not all caps. "GENERIC AUTO LENDER" reads as
 * "Generic Auto Lender". A name that already has lower case ("Capital One") is left
 * exactly as it is, and so are short forms and words with no vowel ("MTG"). Display
 * only: the stored name and the candidate's key never change.
 */
function displayLenderName(name) {
    if (!/[A-Z]/.test(name) || /[a-z]/.test(name))
        return name;
    return name
        .split(/(\s+|-|\/)/)
        .map((part) => {
        if (!/[A-Z]/.test(part))
            return part;
        if (KEEP_UPPER.has(part) || !/[AEIOUY]/.test(part))
            return part;
        return part.charAt(0) + part.slice(1).toLowerCase();
    })
        .join("");
}
const PER_MONTH = {
    weekly: 52 / 12,
    "bi-weekly": 26 / 12,
    monthly: 1,
    quarterly: 1 / 3,
    yearly: 1 / 12,
    irregular: null,
};
/** One stream's payments a month: its latest payment at its own frequency. */
function foundDebtMonthly(item) {
    const factor = PER_MONTH[item.frequency];
    if (factor === null)
        return item.payments12Months / 12;
    return item.latestPaymentAmount * factor;
}
/** J8/J14: what the analysis found, as far as payments can tell. Bills are left out. */
function summarizeFoundDebts(items) {
    const debts = items.filter((i) => !i.treatAsBill);
    return {
        cards: debts.filter((i) => i.debtType === "credit_card").length,
        loans: debts.filter((i) => i.debtType !== "credit_card").length,
        monthly: debts
            .filter((i) => !i.stopped)
            .reduce((sum, i) => sum + foundDebtMonthly(i), 0),
    };
}
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
/**
 * "Found in your analysis: 1 card · 3 loans · $845.00 a month in payments". Null when
 * nothing but bills was found. `money` is each app's own currency formatter.
 */
function debtFoundSummaryCopy(s, money) {
    const parts = [];
    if (s.cards > 0)
        parts.push(plural(s.cards, "card", "cards"));
    if (s.loans > 0)
        parts.push(plural(s.loans, "loan", "loans"));
    if (parts.length === 0)
        return null;
    if (s.monthly > 0)
        parts.push(`${money(s.monthly)} a month in payments`);
    return `Found in your analysis: ${parts.join(" · ")}`;
}
//# sourceMappingURL=debtEliminatorCopy.js.map